import React, {
  createContext,
  type ReactNode,
  useContext,
  useEffect,
  useState,
} from "react";
import type { ErrorScreen } from "../FrontendProvision";
import { getBackendUrl } from "../getBackendUrl";
import { formatUnknownError } from "../notifications/formatError";
import { useStartupSplashController } from "../StartupSplashController";

// Define the structure of the processed configuration object provided by the context
// This will now store parsed values (e.g., actual booleans instead of "true"/"false" strings)
export interface IFrontendConfigSet {
  [key: string]: string | undefined;
}

const ConfigContext = createContext<IFrontendConfigSet | null>(null);

// Overload for useConfig to allow fetching a specific key with a default value
// or the entire config object if no key is provided.
export function useConfig(): IFrontendConfigSet;
export function useConfig<T = string | boolean>(
  // Adjusted default generic type
  key: string,
  defaultValue?: T,
): T | undefined;
export function useConfig<T = string | boolean>(
  // Adjusted default generic type
  key?: string,
  defaultValue?: T,
): IFrontendConfigSet | T | undefined {
  const context = useContext(ConfigContext);
  if (!context) {
    throw new Error("useConfig must be used within a ConfigProvider");
  }
  if (key) {
    if (Object.hasOwn(context, key)) {
      return context[key] as T;
    }
    return defaultValue;
  }
  return context;
}

interface ConfigProviderProps {
  children: ReactNode;
  splashScreen: ReactNode;
  errorScreen: ErrorScreen;
}

export function ConfigProvider({
  children,
  splashScreen: _splashScreen,
  errorScreen,
}: ConfigProviderProps) {
  const [config, setConfig] = useState<IFrontendConfigSet | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const { backendBaseUrl } = getBackendUrl();
  const startupSplash = useStartupSplashController();
  const completeStage = startupSplash?.completeStage;
  const finishStartup = startupSplash?.finishStartup;

  useEffect(() => {
    fetch(`${backendBaseUrl}/frontend-config`)
      .then(async (response) => {
        if (!response.ok) {
          let errorBody = "Unknown error";
          try {
            errorBody = await response.text();
          } catch (_e) {
            // ignore
          }
          throw new Error(
            `Die Frontend-Konfiguration konnte nicht abgerufen werden. Status: ${response.status}. Details: ${errorBody}`,
          );
        }
        return response.json() as Promise<IFrontendConfigSet>; // Use IFrontendConfigSet here
      })
      .then((data: IFrontendConfigSet) => {
        // Assuming the actual key-value pairs are in data.entries
        // And that data.entries is Record<string, string>
        // biome-ignore lint/suspicious/noExplicitAny: legacy code
        (globalThis as any).config = data;
        setConfig(data);
        setIsLoading(false);
        completeStage?.("config");
      })
      .catch((errUnknown) => {
        let errorToSet: Error;
        if (errUnknown instanceof Error) {
          errorToSet = errUnknown;
        } else {
          errorToSet = new Error(String(errUnknown));
        }

        if (
          errorToSet.message.startsWith("Failed to fetch") ||
          errorToSet.message.includes("NetworkError")
        ) {
          errorToSet.message =
            "Leider ist der Server nicht erreichbar. Bitte versuchen Sie es später erneut. Code: ERR_FRONTEND_CONFIG";
        }

        setError(errorToSet);
        setIsLoading(false);
        completeStage?.("config");
        finishStartup?.();
      });
  }, [backendBaseUrl, completeStage, finishStartup]);

  if (error) {
    if (typeof errorScreen === "function") {
      return errorScreen({ error: error });
    }
    if (React.isValidElement(errorScreen)) {
      return React.cloneElement(
        errorScreen as React.ReactElement<{ error?: Error }>,
        { error: error },
      );
    }
    const formatted = formatUnknownError(error, {
      defaultMessage: "Die Frontend-Konfiguration konnte nicht geladen werden.",
      keyPrefix: "frontend-config",
    });
    return (
      <div>
        <p>{formatted.message}</p>
        {formatted.description ? <p>{formatted.description}</p> : null}
        <button type="button" onClick={() => window.location.reload()}>
          Erneut versuchen
        </button>
      </div>
    );
  }
  if (isLoading || !config) {
    return null;
  }

  return (
    <ConfigContext.Provider value={config}>{children}</ConfigContext.Provider>
  );
}
